import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Drawer, Button, Space, Typography, Input, message, Tooltip } from 'antd';
import { SaveOutlined, CloseOutlined, BgColorsOutlined, CheckOutlined } from '@ant-design/icons';
import { useStore } from '@/stores';
import { useHoldUnsavedWork } from '@/components/practiceSwitch';
import { colors } from '@/theme/tokens';
import { updateAgent } from '@/api/agents';
import type { AgentDefinition } from '@/api/types';

const { Text, Paragraph } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// AgentColorEditorDrawer — pick the agent's accent color.
//
// Why it matters: the color shows up in chat bubbles, hero accents, and
// list-page swatches across surfaces. A small visual identity that makes
// the per-agent UI feel intentional.
//
// UX:
//   • Curated palette of 12 brand-aligned swatches (the same palette the
//     rest of the app uses for accents). One click selects.
//   • Custom hex input below for the rare "I want a specific brand color"
//     case. Validated against #RRGGBB / #RGB.
//   • Live "Preview" card showing how the color reads against light + dark
//     backgrounds, so the author doesn't pick something illegible.
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  agent: AgentDefinition | null;
  onClose: () => void;
  onSaved?: (next: AgentDefinition) => void | Promise<void>;
}

// Curated palette — pulled from theme tokens so swatches match the rest of
// the app's accent vocabulary. 12 picks: 6 brand-saturated + 6 lighter
// variants for agents whose vibe should feel friendlier.
const PALETTE: { name: string; value: string }[] = [
  { name: 'Violet', value: colors.violet[600] },
  { name: 'Ink', value: colors.ink[600] },
  { name: 'Green', value: colors.green[600] },
  { name: 'Amber', value: colors.amber[600] },
  { name: 'Red', value: colors.red[600] },
  { name: 'Slate', value: colors.slate[600] },
  { name: 'Soft violet', value: colors.violet[500] },
  { name: 'Soft ink', value: colors.ink[500] },
  { name: 'Soft green', value: colors.green[500] },
  { name: 'Soft amber', value: colors.amber[500] },
  { name: 'Soft red', value: colors.red[500] },
  { name: 'Soft slate', value: colors.slate[500] },
];

const HEX_REGEX = /^#([0-9a-fA-F]{3}){1,2}$/;

export const AgentColorEditorDrawer = observer(function AgentColorEditorDrawer({
  open,
  agent,
  onClose,
  onSaved,
}: Props) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  const [color, setColor] = useState<string>(agent?.color ?? PALETTE[0].value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setColor(agent?.color ?? PALETTE[0].value);
    setError(null);
  }, [open, agent?.id, agent?.color]);

  const isValid = HEX_REGEX.test(color);
  const dirty = (agent?.color ?? '') !== color;
  useHoldUnsavedWork(open && dirty, `color for ${agent?.displayName ?? 'this agent'}`);

  const handleSave = async () => {
    if (!agent) return;
    if (!isValid) {
      setError('Color must be a hex value like #7C3AED.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const next = await updateAgent({ ...agent, color });
      void message.success('Color saved.');
      if (onSaved) await onSaved(next);
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      title={
        <Space size={8}>
          <BgColorsOutlined style={{ color: colors.violet[500] }} />
          <span>Agent color</span>
        </Space>
      }
      width={460}
      open={open}
      onClose={onClose}
      destroyOnHidden
      extra={
        <Space>
          <Button icon={<CloseOutlined />} onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={handleSave}
            disabled={!dirty || !isValid}
          >
            Save
          </Button>
        </Space>
      }
    >
      {!agent ? null : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <Paragraph type="secondary" style={{ fontSize: 13, marginBottom: 0 }}>
            The agent's accent color shows in chat bubbles, hero strips, and the
            list-page swatch. Pick from the brand palette or paste a hex value.
          </Paragraph>

          {/* Curated palette */}
          <div>
            <Text strong style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.06, color: isDark ? colors.slate[400] : colors.slate[500] }}>
              Palette
            </Text>
            <div
              style={{
                marginTop: 8,
                display: 'grid',
                gridTemplateColumns: 'repeat(6, 1fr)',
                gap: 10,
              }}
            >
              {PALETTE.map((p) => {
                const selected = color.toLowerCase() === p.value.toLowerCase();
                return (
                  <Tooltip key={p.value} title={`${p.name} (${p.value})`}>
                    <button
                      type="button"
                      onClick={() => setColor(p.value)}
                      aria-label={p.name}
                      style={{
                        cursor: 'pointer',
                        border: selected
                          ? `2px solid ${isDark ? colors.slate[100] : colors.slate[900]}`
                          : `2px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
                        borderRadius: 10,
                        padding: 0,
                        height: 44,
                        background: p.value,
                        position: 'relative',
                        outline: 'none',
                        transition: 'transform 100ms ease, border-color 120ms ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.04)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    >
                      {selected && (
                        <CheckOutlined
                          style={{
                            color: '#FFFFFF',
                            fontSize: 16,
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.4))',
                          }}
                        />
                      )}
                    </button>
                  </Tooltip>
                );
              })}
            </div>
          </div>

          {/* Custom hex */}
          <div>
            <Text strong style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.06, color: isDark ? colors.slate[400] : colors.slate[500] }}>
              Custom hex
            </Text>
            <Space.Compact style={{ width: '100%', marginTop: 8 }}>
              <Input
                value={color}
                onChange={(e) => setColor(e.target.value.trim())}
                placeholder="#7C3AED"
                prefix={
                  <span
                    aria-hidden
                    style={{
                      display: 'inline-block',
                      width: 14,
                      height: 14,
                      borderRadius: 4,
                      background: isValid ? color : 'transparent',
                      border: `1px solid ${isDark ? colors.slate[600] : colors.slate[300]}`,
                    }}
                  />
                }
                status={!isValid ? 'error' : undefined}
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              />
            </Space.Compact>
            {!isValid && (
              <Text type="danger" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                Hex required (e.g. #7C3AED or #abc).
              </Text>
            )}
          </div>

          {/* Live preview against light + dark surfaces */}
          <div>
            <Text strong style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.06, color: isDark ? colors.slate[400] : colors.slate[500] }}>
              Preview
            </Text>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 8 }}>
              <PreviewSurface bg="#FFFFFF" textColor={colors.slate[900]} accent={isValid ? color : undefined} label="Light" agent={agent} />
              <PreviewSurface bg={colors.slate[800]} textColor={colors.slate[50]} accent={isValid ? color : undefined} label="Dark" agent={agent} />
            </div>
          </div>

          {error && (
            <Text type="danger" style={{ fontSize: 13 }}>
              {error}
            </Text>
          )}
        </div>
      )}
    </Drawer>
  );
});

// ── Preview swatch — fake mini agent card ────────────────────────────────

function PreviewSurface({
  bg,
  textColor,
  accent,
  label,
  agent,
}: {
  bg: string;
  textColor: string;
  accent: string | undefined;
  label: string;
  agent: AgentDefinition;
}) {
  return (
    <div
      style={{
        background: bg,
        color: textColor,
        border: `1px solid ${colors.slate[200]}`,
        borderLeft: accent ? `4px solid ${accent}` : `4px solid ${colors.slate[200]}`,
        borderRadius: 10,
        padding: '10px 12px',
        minHeight: 80,
      }}
    >
      <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.06, opacity: 0.7 }}>
        {label}
      </div>
      <div style={{ fontWeight: 700, fontSize: 14, marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {agent.displayName ?? `Agent #${agent.id}`}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
        <span
          aria-hidden
          style={{
            display: 'inline-block',
            width: 10,
            height: 10,
            borderRadius: 999,
            background: accent ?? colors.slate[300],
          }}
        />
        <span style={{ fontSize: 11, opacity: 0.7 }}>
          chat bubble · pill · accent
        </span>
      </div>
    </div>
  );
}
